import { useEffect, useState } from 'react';

type StatusType = 'idle' | 'loading' | 'ready' | 'error';
interface KVP<T> {
  [key: string]: T;
}

const scriptCache: KVP<HTMLScriptElement> = {};

export const useScript = (url: string | undefined, attributes: KVP<string> = {}, element?: HTMLElement | null): StatusType => {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = useState<StatusType>(url ? 'loading' : 'idle');

  useEffect(() => {
    // return if the url isn;t present or if the element is null
    // the element will be null if a ref is used and not yet populated
    // elements not passed in will be undefined
    if (!url || element === null) {
      setStatus('idle');
      return;
    }
    // Fetch existing script element by src
    // It may have been added by another instance of this hook
    const scriptExists = !!scriptCache[url];
    const script = scriptCache[url] ?? document.createElement('script');
    const targetElement = element ?? document.body;

    if (!scriptExists) {
      script.src = url;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      Object.keys(attributes).forEach((key) => {
        script.setAttribute(key, attributes[key]);
      });
      scriptCache[url] = script;
      targetElement.appendChild(script);
    } else {
      // if react cleared the script re-append the same instance of it
      const domScript = document.querySelector(`script[src="${url}"]`) as HTMLScriptElement | undefined;
      if (!domScript) {
        targetElement.appendChild(script);
      }
      // Grab existing script status from attribute and set to state.
      setStatus(script.getAttribute('data-status') as StatusType);
    }
    // Store status in attribute on script
    // This can be read by other instances of this hook
    const setAttributeFromEvent = (event: Event) => {
      script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
    };
    script.addEventListener('load', setAttributeFromEvent);
    script.addEventListener('error', setAttributeFromEvent);

    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event: Event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };
    // Add event listeners
    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);

    return () => {
      script.removeEventListener('load', setAttributeFromEvent);
      script.removeEventListener('error', setAttributeFromEvent);
      script.removeEventListener('load', setStateFromEvent);
      script.removeEventListener('error', setStateFromEvent);
      // targetElement.removeChild(script);
      script.parentElement?.removeChild(script);
    };
  }, [url, attributes, element]);

  return status;
};
